import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Eye } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const STATUSES = ["pending", "confirmed", "preparing", "ready", "out_for_delivery", "delivered", "cancelled"];
const TONE = { pending: "amber", confirmed: "blue", preparing: "blue", ready: "blue", out_for_delivery: "blue", delivered: "emerald", cancelled: "red" };

export default function Orders() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orders");
        if (!cancelled) setItems(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Order ${status.replaceAll("_", " ")}`);
      reload();
      if (view && view.id === id) setView({ ...view, status });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (q && !`${o.order_no} ${o.customer_name} ${o.customer_phone}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <PageHeader eyebrow="E-commerce" title="Online Orders"
        subtitle="Orders placed by customers on the public storefront. Update status as they progress." />
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search order #, customer…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="order-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="order-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-gray-500">{filtered.length} order(s)</span>
      </div>
      {filtered.length === 0 ? (
        <EmptyState title="No orders yet" hint="Orders placed on the public storefront will appear here in real time." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="min-w-full text-sm" data-testid="orders-table">
            <thead className="bg-gray-50">
              <tr>{["Order #", "Date", "Customer", "Phone", "Items", "Total", "Status", ""].map((h) => (
                <th key={h} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{o.order_no}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{o.customer_name}</td>
                  <td className="px-4 py-3 text-gray-700">{o.customer_phone}</td>
                  <td className="px-4 py-3 text-gray-700">{(o.items || []).length}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-900">{o.currency} {Number(o.total || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><Badge tone={TONE[o.status] || "slate"}>{(o.status || "").replaceAll("_", " ")}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setView(o)} data-testid={`order-view-${o.id}`}>
                      <Eye size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <Dialog open onOpenChange={(v) => !v && setView(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Order {view.order_no}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <KV label="Customer" value={view.customer_name} />
                <KV label="Phone" value={view.customer_phone} />
                <KV label="Email" value={view.customer_email} />
                <KV label="Branch" value={view.branch} />
                <KV label="Payment" value={view.payment_method?.replaceAll("_", " ")} />
                <KV label="Status" value={<Badge tone={TONE[view.status] || "slate"}>{(view.status || "").replaceAll("_", " ")}</Badge>} />
              </div>
              {view.delivery_address && <KV label="Delivery address" value={view.delivery_address} />}
              {view.notes && <KV label="Notes" value={view.notes} />}
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-1.5">Items</div>
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                  {(view.items || []).map((it) => (
                    <li key={it.product_id} className="flex justify-between px-3 py-2">
                      <span>{it.name} × {it.quantity}</span>
                      <span className="font-medium">{view.currency} {Number(it.line_total).toLocaleString()}</span>
                    </li>
                  ))}
                  <li className="flex justify-between px-3 py-2 font-bold bg-gray-50">
                    <span>Total</span>
                    <span>{view.currency} {Number(view.total || 0).toLocaleString()}</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-2 pt-3">
                {STATUSES.filter((s) => s !== view.status).map((s) => (
                  <Button key={s} size="sm" variant="outline"
                    onClick={() => updateStatus(view.id, s)}
                    data-testid={`order-set-${s}`}>
                    Mark {s.replaceAll("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase text-gray-500">{label}</div>
      <div className="text-gray-900">{value || "—"}</div>
    </div>
  );
}
